import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import Sidebar from '@/components/Sidebar';
import { ArrowLeft, Loader2, RefreshCw, AlertCircle, PlusCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { startAtsJobsSync, fetchAtsJobs } from '@/services/knitService';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AtsJob {
  id: number;
  ats_job_id: string;
  title: string;
  description: string;
  status: string;
  job_info_descriptions: any;
  created_at: string;
  updated_at: string;
  raw_data: any;
  hasAssessment?: boolean;
  assessmentId?: string;
}

const AtsJobs: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<AtsJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  
  useEffect(() => {
    if (user) {
      fetchJobsData();
    } else {
      setLoading(false);
      setError("Please log in to view ATS jobs");
    }
  }, [user]);
  
  const fetchJobsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch ATS jobs
      const jobsData = await fetchAtsJobs();
      
      if (jobsData.length === 0) {
        setJobs([]);
        setLoading(false);
        return;
      }
      
      // Get all assessment IDs for ats_job_ids
      const atsJobIds = jobsData.map(job => job.ats_job_id);
      
      const { data: existingAssessments, error: assessmentsError } = await supabase
        .from('assessments')
        .select('id, ats_job_id')
        .eq('source', 'ATS')
        .in('ats_job_id', atsJobIds);
        
      if (assessmentsError) {
        console.error('Error fetching existing assessments:', assessmentsError);
        throw assessmentsError;
      }
      
      // Map to create a lookup object for quick access
      const assessmentMap: Record<string, string> = {};
      existingAssessments?.forEach(assessment => {
        assessmentMap[assessment.ats_job_id] = assessment.id;
      });
      
      // Add hasAssessment flag to jobs
      const enhancedJobs = jobsData.map(job => ({
        ...job,
        hasAssessment: assessmentMap.hasOwnProperty(job.ats_job_id),
        assessmentId: assessmentMap[job.ats_job_id]
      }));
      
      setJobs(enhancedJobs);
    } catch (error: any) {
      console.error('Error fetching ATS jobs:', error);
      setError(error.message || 'Failed to load ATS jobs');
      toast.error('Failed to load ATS jobs: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSyncJobs = async () => {
    try {
      setSyncLoading(true);
      const result = await startAtsJobsSync();
      
      if (result.success) {
        toast.success(result.message);
        // Refresh the jobs list after a sync is initiated
        await fetchJobsData();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Error syncing ATS jobs:', error);
      toast.error('Error syncing ATS jobs: ' + (error.message || 'Unknown error'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCreateAssessment = (job: AtsJob) => {
    navigate('/assessments/create', { 
      state: { 
        jobTitle: job.title,
        source: 'ATS',
        atsJobId: job.ats_job_id
      } 
    });
  };
  
  const handleViewAssessment = (assessmentId: string) => {
    navigate(`/assessments/${assessmentId}`);
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8 animate-fade-in">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/assessments')} 
                className="p-2 mr-4"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">ATS Jobs</h1>
                <p className="text-muted-foreground">View and manage job listings from your ATS</p>
              </div>
            </div>
            <Button 
              onClick={handleSyncJobs} 
              disabled={syncLoading}
              className="gradient-button"
            >
              {syncLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Sync Jobs
            </Button>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
              <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>Error: {error}</span>
              </div>
              <p className="mb-4 text-muted-foreground">
                There was a problem loading the job data. Please check your connection and try again.
              </p>
              <Button onClick={fetchJobsData}>
                Retry
              </Button>
            </div>
          ) : jobs.length > 0 ? (
            <Table>
              <TableCaption>List of jobs imported from your ATS</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.title || 'Untitled'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        job.status === 'OPEN' ? 'bg-green-100 text-green-800' : 
                        job.status === 'CLOSED' ? 'bg-red-100 text-red-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status || 'Unknown'}
                      </span>
                    </TableCell>
                    <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(job.updated_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {job.hasAssessment ? (
                        <Button 
                          onClick={() => handleViewAssessment(job.assessmentId!)}
                          size="sm"
                          className="flex items-center bg-blue-600 text-white hover:bg-blue-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Assessment
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleCreateAssessment(job)}
                          size="sm"
                          className="flex items-center gradient-button"
                        >
                          <PlusCircle className="w-4 h-4 mr-1" />
                          Create Assessment
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold mb-2">No jobs found</h3>
              <p className="text-muted-foreground mb-4">
                Connect your ATS and sync job data to view your job listings here
              </p>
              <Button 
                onClick={handleSyncJobs} 
                disabled={syncLoading}
                className="gradient-button"
              >
                {syncLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Sync Jobs
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AtsJobs; 